import { AlertCircle, Code2, ExternalLink } from 'lucide-react';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/card';
import { useDevMode } from '../../hooks/useDevMode';

interface DevPage {
    path: string;
    title: string;
    description?: string;
    status: 'in-progress' | 'draft' | 'ready';
    category?: string;
}

const DevPlaygroundPage: React.FC = () => {
    const navigate = useNavigate();
    const { isUnlocked } = useDevMode();

    // Redirect nếu chưa unlock dev mode
    useEffect(() => {
        if (!isUnlocked) {
            navigate('/', { replace: true });
        }
    }, [isUnlocked, navigate]);

    // Không render nếu chưa unlock
    if (!isUnlocked) {
        return null;
    }

    // Danh sách các trang đang develop
    // Bạn có thể thêm/bớt các trang ở đây
    const devPages: DevPage[] = [
        {
            path: '/merchant-home',
            title: 'Merchant Home',
            description: 'Dashboard home page cho merchant với metrics, charts, insights và AI tips',
            status: 'ready',
            category: 'Business Management'
        },
        {
            path: '/merchant-heatmap',
            title: 'Merchant Heatmap',
            description: 'Coupon redemption analytics với heat map visualization và neighborhood analysis',
            status: 'ready',
            category: 'Business Management'
        },
        {
            path: '/business-profile',
            title: 'Business Profile',
            description: 'Quản lý thông tin doanh nghiệp, logo, giờ làm việc và mạng xã hội',
            status: 'ready',
            category: 'Business Management'
        },
        {
            path: '/merchant-profile',
            title: 'User Profile',
            description: 'Quản lý thông tin cá nhân của merchant: tên, email, số điện thoại, giới tính và ngôn ngữ',
            status: 'ready',
            category: 'Business Management'
        },
        {
            path: '/merchant-coupon',
            title: 'Create Coupon',
            description: 'Tạo coupon mới với các tùy chọn discount, giới hạn sử dụng, điều khoản và điều kiện',
            status: 'ready',
            category: 'Business Management'
        },
        // Ví dụ - bạn có thể thêm các trang của mình vào đây
        // {
        //     path: '/your-new-page',
        //     title: 'Your New Page',
        //     description: 'Mô tả trang mới',
        //     status: 'in-progress',
        //     category: 'Feature'
        // },
    ];

    const getStatusBadge = (status: DevPage['status']) => {
        const styles = {
            'in-progress': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
            'draft': 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
            'ready': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
        };

        const labels = {
            'in-progress': 'Đang phát triển',
            'draft': 'Bản nháp',
            'ready': 'Sẵn sàng',
        };

        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
                {labels[status]}
            </span>
        );
    };

    const groupedPages = devPages.reduce((acc, page) => {
        const category = page.category || 'Khác';
        if (!acc[category]) {
            acc[category] = [];
        }
        acc[category].push(page);
        return acc;
    }, {} as Record<string, DevPage[]>);

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <div className="mb-6">
                <div className="flex items-center gap-3 mb-2">
                    <Code2 className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                        Dev Playground
                    </h1>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mt-2">
                    Danh sách các trang đang được phát triển. Click vào bất kỳ trang nào để điều hướng.
                </p>
            </div>

            {devPages.length === 0 ? (
                <Card>
                    <CardContent className="pt-6">
                        <div className="text-center py-12">
                            <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                Chưa có trang nào được thêm vào
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400 mb-4">
                                Thêm các trang đang phát triển vào file <code className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">DevPlaygroundPage.tsx</code>
                            </p>
                            <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg text-left max-w-2xl mx-auto">
                                <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                                    <strong>Ví dụ:</strong>
                                </p>
                                <pre className="text-xs bg-gray-900 text-gray-100 p-3 rounded overflow-x-auto">
                                    {`const devPages: DevPage[] = [
    {
        path: '/your-new-page',
        title: 'Your New Page',
        description: 'Mô tả trang mới',
        status: 'in-progress',
        category: 'Feature'
    },
];`}
                                </pre>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-6">
                    {Object.entries(groupedPages).map(([category, pages]) => (
                        <div key={category}>
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                                {category}
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {pages.map((page) => (
                                    <Card
                                        key={page.path}
                                        className="hover:shadow-lg transition-shadow cursor-pointer"
                                        onClick={() => navigate(page.path)}
                                    >
                                        <CardHeader>
                                            <div className="flex items-start justify-between gap-2">
                                                <CardTitle className="text-lg !text-base">{page.title}</CardTitle>
                                                {getStatusBadge(page.status)}
                                            </div>
                                            {page.description && (
                                                <CardDescription className="mt-2">
                                                    {page.description}
                                                </CardDescription>
                                            )}
                                        </CardHeader>
                                        <CardContent>
                                            <div className="flex items-center justify-between">
                                                <code className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                                                    {page.path}
                                                </code>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(page.path);
                                                    }}
                                                >
                                                    <ExternalLink className="h-4 w-4 mr-1" />
                                                    Mở
                                                </Button>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                    <div>
                        <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-1">
                            Lưu ý
                        </h3>
                        <p className="text-sm text-blue-800 dark:text-blue-200">
                            Trang này chỉ nên được truy cập trong môi trường development.
                            Để ẩn trang này trong production, hãy kiểm tra biến môi trường <code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">REACT_APP_ENV</code>.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DevPlaygroundPage;

